import type { User } from "firebase/auth";

export type IntelligenceCapacityId = "lite" | "plus" | "extra";

export interface IntelligenceCatalogItem {
  id: IntelligenceCapacityId;
  name: string;
  monthlyCredits: number;
  monthlyPrice: number;
  currency: string;
  marketingFeatures?: string[];
  sortOrder?: number;
  stripeProductId?: string | null;
  stripePriceId?: string | null;
  updatedAt?: string | null;
}

const getBackendBaseUrl = (): string => {
  const raw = process.env.REACT_APP_BACKEND_URL;
  if (!raw) {
    throw new Error("Missing REACT_APP_BACKEND_URL environment variable.");
  }
  return raw.replace(/\/+$/, "");
};

const parseError = async (response: Response): Promise<string> => {
  const data = (await response.json().catch(() => null)) as unknown;
  if (data && typeof data === "object" && "detail" in data) {
    const detail = (data as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
    if (detail && typeof detail === "object" && "message" in detail) {
      return String((detail as { message: unknown }).message);
    }
  }
  return `Backend error (${response.status}).`;
};

export const fetchIntelligenceCatalog = async (
  user: User
): Promise<IntelligenceCatalogItem[]> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const response = await fetch(`${baseUrl}/api/v1/platform/intelligence/catalog`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error(await parseError(response));
  const data = (await response.json().catch(() => null)) as unknown;
  return Array.isArray(data) ? (data as IntelligenceCatalogItem[]) : [];
};

export const updateIntelligenceCatalogItem = async (
  user: User,
  capacityId: IntelligenceCapacityId,
  payload: {
    name: string;
    monthlyCredits: number;
    monthlyPrice: number;
    currency?: string;
    marketingFeatures?: string[];
    sortOrder?: number;
    stripeProductId?: string | null;
    stripePriceId?: string | null;
    syncStripe?: boolean;
  }
): Promise<IntelligenceCatalogItem> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const response = await fetch(
    `${baseUrl}/api/v1/platform/intelligence/catalog/${capacityId}`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );
  if (!response.ok) throw new Error(await parseError(response));
  return (await response.json()) as IntelligenceCatalogItem;
};

/** Create/reuse Stripe products+prices for all Intelligence capacities. */
export const initializeIntelligenceCatalogOnStripe = async (
  user: User
): Promise<IntelligenceCatalogItem[]> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const response = await fetch(
    `${baseUrl}/api/v1/platform/intelligence/catalog/initialize-stripe`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (!response.ok) throw new Error(await parseError(response));
  const data = (await response.json().catch(() => null)) as unknown;
  return Array.isArray(data) ? (data as IntelligenceCatalogItem[]) : [];
};

export const syncIntelligenceCatalogItemStripe = async (
  user: User,
  capacityId: IntelligenceCapacityId
): Promise<IntelligenceCatalogItem> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const response = await fetch(
    `${baseUrl}/api/v1/platform/intelligence/catalog/${capacityId}/sync-stripe`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (!response.ok) throw new Error(await parseError(response));
  return (await response.json()) as IntelligenceCatalogItem;
};

export const unlinkIntelligenceCatalogItemStripe = async (
  user: User,
  capacityId: IntelligenceCapacityId
): Promise<IntelligenceCatalogItem> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const response = await fetch(
    `${baseUrl}/api/v1/platform/intelligence/catalog/${capacityId}/unlink-stripe`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (!response.ok) throw new Error(await parseError(response));
  return (await response.json()) as IntelligenceCatalogItem;
};

export const applyIntelligenceCatalogDefaults = async (
  user: User,
  payload: {
    capacityIds?: IntelligenceCapacityId[];
    clearStripeIds?: boolean;
    syncStripe?: boolean;
  } = {}
): Promise<IntelligenceCatalogItem[]> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const response = await fetch(
    `${baseUrl}/api/v1/platform/intelligence/catalog/apply-defaults`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );
  if (!response.ok) throw new Error(await parseError(response));
  const data = (await response.json().catch(() => null)) as unknown;
  return Array.isArray(data) ? (data as IntelligenceCatalogItem[]) : [];
};
